import { useMemo } from "react";

import { CollectionItem } from "@/api";
import { PageLoader } from "@/components";
import { useMainTabs } from "@/hooks";
import {
  isCollectionOfCollections,
  isHeroCarousel,
  isInProgressTemplate,
} from "@/utils";
import { TEMPLATE_MIXED } from "@/const.ts";

import { MainTabSlider } from "./components/MainTabSlider.tsx";
import { MainTabGrid } from "./components/MainTabGrid.tsx";
import { MainTabHeroCarousel } from "./components/MainTabHeroCarousel.tsx";
import { InProgressSlider } from "./components/InProgressSlider.tsx";

interface ContentTabProps {
  id: number;
}

/**
 * This component renders content on a main tab or the subtab of a main tab
 * A list of sliders in most cases, but falls back to a grid otherwise
 * @param id - The Collection ID of the Tab or SubTab being displayed
 */
export const ContentTab = ({ id }: ContentTabProps) => {
  const { collectionsById, mainTabs } = useMainTabs();
  const collection = useMemo(() => {
    const collection = collectionsById && collectionsById[id];
    if (collection) return collection; // Subtab

    // Else try a main tab
    return mainTabs?.find((t) => t.TabId === id);
  }, [collectionsById, mainTabs, id]);

  const isOnlyCollections = useMemo(() => {
    if (!collection) {
      return false;
    }
    return isCollectionOfCollections(collection);
  }, [collection]);

  if (!collectionsById || Object.keys(collectionsById).length == 0) {
    return <PageLoader />;
  }
  if (!collection) {
    console.debug("Hiding collection not in Main Tabs", { tabId: id });
    return <PageLoader />;
  }

  return (
    <div className="px-[28px] text-item-primary">
      {isOnlyCollections ? (
        <div>
          {collection?.Items.map((i: CollectionItem, idx) => {
            const col = collectionsById[parseInt(i.ChildId)];
            if (!col) {
              console.error("Cant find collection in MainTab response", {
                CollectionId: i.ChildId,
              });
              return null;
            }

            {
              /* This can have no items so needs to be before that check */
            }
            if (isInProgressTemplate(col.TemplateId)) {
              return (
                <InProgressSlider
                  data-testid={`in-progress-slider-${idx}`}
                  className="mb-[52px]"
                  key={i.ChildId}
                />
              );
            }

            if (!col.Items || col.Items.length == 0) {
              console.debug("Hiding collection with no items", {
                CollectionId: col.TabId,
              });
              return null;
            }

            if (isHeroCarousel(col.TemplateId)) {
              return (
                <div className="mb-[52px]" key={i.ChildId}>
                  <MainTabHeroCarousel
                    data-testid={`hero-carousel-${idx}`}
                    collection={col}
                  />
                </div>
              );
            }

            // Slider
            return (
              <div className="mb-[52px]" key={i.ChildId}>
                <MainTabSlider
                  data-testid={`slider-${idx}`}
                  collection={col}
                  templateId={i.TemplateId ?? ""}
                  isMixedLayout={collection.TemplateId === TEMPLATE_MIXED}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <MainTabGrid collection={collection} items={collection?.Items} />
      )}
    </div>
  );
};
